import { Link as RouterLink } from "react-router-dom";
import { Link, Typography } from "@mui/material";
import type { InvoiceId } from "../api/types";
import { useRegistry } from "../context/RegistryContext";
import { invoicePath } from "../logic/invoice-path";
import { SenderHeading } from "./SenderHeading";

export type LinkedInvoiceProps = {
  /** The invoice a transaction is linked to, if any. */
  invoiceId: InvoiceId | null | undefined;
};

/**
 * A transaction's invoice, read-only: who sent it (a payee or one of your own
 * accounts) and the invoice's name, which opens the invoice's view. The link
 * is set by an invoice rule or an import, not edited here.
 */
export const LinkedInvoice = ({ invoiceId }: LinkedInvoiceProps) => {
  // The registry carries hidden invoices too, so this only misses a deleted
  // one. Its projection lacks the amounts, KID and pay-to account, but name
  // and sender are all this row shows.
  const { getInvoice, loading } = useRegistry();

  if (!invoiceId) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        None
      </Typography>
    );
  }

  const invoice = getInvoice(invoiceId);
  // Not "deleted" just because the registry hasn't arrived yet.
  if (!invoice && loading) return null;
  // Gone, so there is nothing to open — say so rather than link to an empty
  // view, as the applied-rules menu does for a deleted invoice's rule.
  if (!invoice) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Deleted invoice
      </Typography>
    );
  }

  return (
    <SenderHeading
      invoice={invoice}
      size="sm"
      subtitle={
        <Link
          component={RouterLink}
          to={invoicePath(invoice.id)}
          variant="body2"
          underline="hover"
        >
          {invoice.name}
        </Link>
      }
    />
  );
};
